import React, { useState } from "react";
import { toast } from "sonner";
import { Mail, MapPin, Phone, Send, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import api, { formatApiErrorDetail } from "@/lib/api";

const initial = { full_name: "", email: "", phone: "", subject: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message || "Message envoyé");
      setForm(initial);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white">
      <section className="pt-20 md:pt-28 pb-16">
        <div className="mx-auto max-w-7xl px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#2196F3] mb-4">Contact</div>
          <h1 className="font-display text-4xl md:text-6xl font-extrabold tracking-tight text-slate-900 max-w-3xl leading-[1.05]">
            Parlons de votre projet de nettoyage.
          </h1>
          <p className="mt-6 text-lg text-slate-600 max-w-2xl">
            Une question, un besoin spécifique, une demande d'intervention urgente ? Notre équipe vous répond dans la journée.
          </p>
        </div>
      </section>

      <section className="pb-24">
        <div className="mx-auto max-w-7xl px-6 md:px-10 grid lg:grid-cols-[1fr_1.4fr] gap-10">
          {/* Left info */}
          <div className="space-y-6">
            <div className="bg-[#1976D2] text-white rounded-3xl p-8 relative overflow-hidden grain">
              <div className="relative z-10">
                <div className="text-xs uppercase tracking-[0.25em] text-[#64B5F6] font-semibold mb-4">Coordonnées</div>
                <h2 className="font-display text-2xl font-bold mb-6">GIGA CLEAN SERVICE</h2>
                <div className="space-y-5 text-sm">
                  <div className="flex items-start gap-3">
                    <div className="h-9 w-9 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                      <MapPin className="h-4 w-4 text-[#64B5F6]" />
                    </div>
                    <div>
                      <div className="text-white/60 text-xs mb-0.5">Adresse</div>
                      <div className="text-white">RN370 Espace Godard<br />95500 Gonesse, France</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="h-9 w-9 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                      <Mail className="h-4 w-4 text-[#64B5F6]" />
                    </div>
                    <div>
                      <div className="text-white/60 text-xs mb-0.5">Email</div>
                      <a href="mailto:gigacleanservice@outlook.fr" className="text-white hover:text-[#64B5F6] break-all" data-testid="contact-email-link">
                        gigacleanservice@outlook.fr
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="h-9 w-9 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                      <Building2 className="h-4 w-4 text-[#64B5F6]" />
                    </div>
                    <div>
                      <div className="text-white/60 text-xs mb-0.5">Direction</div>
                      <div className="text-white">Sindou Kamagate<br />Directeur & Fondateur</div>
                    </div>
                  </div>
                </div>
              </div>
              <div aria-hidden className="absolute -bottom-16 -right-16 h-52 w-52 rounded-full bg-[#64B5F6]/25 blur-3xl" />
            </div>

            <div className="bg-slate-50 rounded-3xl p-8 border border-slate-100">
              <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#2196F3] mb-3">Horaires</div>
              <h3 className="font-display font-bold text-lg text-slate-900 mb-4">Disponibilité équipe</h3>
              <div className="text-sm text-slate-600 space-y-1.5">
                <div className="flex justify-between"><span>Lundi – Vendredi</span><strong className="text-slate-900">6h – 20h</strong></div>
                <div className="flex justify-between"><span>Samedi</span><strong className="text-slate-900">8h – 18h</strong></div>
                <div className="flex justify-between"><span>Dimanche</span><strong className="text-slate-900">Sur demande</strong></div>
              </div>
            </div>
          </div>

          {/* Right form */}
          <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-8 md:p-10">
            <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="cfname">Nom complet *</Label>
                  <Input id="cfname" required value={form.full_name} onChange={set("full_name")} className="h-12" data-testid="contact-fullname" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cemail">Email *</Label>
                  <Input id="cemail" type="email" required value={form.email} onChange={set("email")} className="h-12" data-testid="contact-email" />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="cphone">Téléphone</Label>
                  <Input id="cphone" value={form.phone} onChange={set("phone")} className="h-12" data-testid="contact-phone" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="csubject">Sujet *</Label>
                  <Input id="csubject" required value={form.subject} onChange={set("subject")} className="h-12" placeholder="Ex : demande d'information" data-testid="contact-subject" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="cmessage">Message *</Label>
                <Textarea id="cmessage" required rows={6} value={form.message} onChange={set("message")} placeholder="Votre message…" data-testid="contact-message" />
              </div>
              <div className="pt-2">
                <Button type="submit" disabled={loading} className="w-full md:w-auto rounded-full bg-[#2196F3] hover:bg-[#1976D2] text-white h-12 px-10" data-testid="contact-submit">
                  {loading ? "Envoi…" : (<>Envoyer le message <Send className="ml-2 h-4 w-4" /></>)}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
